import {
  ReactNode,
  useState,
  useEffect,
  useRef,
  cloneElement,
  isValidElement,
} from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "../../base/Tooltip/Tooltip";
import { Modal } from "../../base/Modal/Modal";
import { Button, PRESS_DEPTH } from "../../base/Button/Button";
import { Snackbar } from "../../base/Snackbar/Snackbar";
import {
  RightRunIcon,
  FolderIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  FilePlusIcon,
  CloseIcon,
  MonitorIcon,
  SaveIcon,
  CheckIcon,
  LanguageIcon,
  SettingsIcon,
} from "../../base/Icon";

// 頭文字を大文字にするだけでは正しく書けない名前をここに置く。
// 落ちると「Typescript」「Html」のような表記になって目に付く
const LANGUAGE_LABELS: Record<string, string> = {
  python: "Python",
  javascript: "JavaScript",
  js: "JavaScript",
  typescript: "TypeScript",
  ts: "TypeScript",
  java: "Java",
  go: "Go",
  golang: "Go",
  ruby: "Ruby",
  php: "PHP",
  html: "HTML",
  css: "CSS",
  sql: "SQL",
  bash: "Bash",
  c: "C",
  cpp: "C++",
};

function languageLabel(key: string): string {
  return (
    LANGUAGE_LABELS[key.toLowerCase()] ??
    key.charAt(0).toUpperCase() + key.slice(1)
  );
}

function LanguagePicker({
  languages,
  value,
  onChange,
}: {
  languages: string[];
  value: string;
  onChange: (lang: string) => void;
}) {
  const [open, setOpen] = useState(false);
  /* 操作の帯は画面の下端にあるので、下に十分な空きが無ければ釦の上に開く */
  const [menuPos, setMenuPos] = useState<{
    top?: number;
    bottom?: number;
    right: number;
  } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  // 親要素に overflow-hidden が付いている文脈でも見えるよう、メニューは body に Portal
  const updateMenuPos = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    // 窓の高さは 1 行 40px と上下の余白 8px。実測せずに先に決めて、開く向きを描く前に定める
    const menuHeight = languages.length * 40 + 10;
    const roomBelow = window.innerHeight - rect.bottom - 4;
    if (roomBelow < menuHeight && rect.top > roomBelow) {
      setMenuPos({ bottom: window.innerHeight - rect.top + 4, right });
    } else {
      setMenuPos({ top: rect.bottom + 4, right }); // mt-1 相当
    }
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPos();
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleScrollOrResize = () => updateMenuPos();
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 items-center gap-2 rounded-lg border border-main bg-main px-3 text-sm font-medium text-main transition-colors hover:bg-second focus:outline-none focus:ring-2 focus:ring-primary"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="プログラミング言語を選択"
      >
        <LanguageIcon language={value} size={18} />
        <span>{languageLabel(value)}</span>
        <ChevronDownIcon
          className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open &&
        menuPos &&
        typeof document !== "undefined" &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            style={{
              position: "fixed",
              top: menuPos.top,
              bottom: menuPos.bottom,
              right: menuPos.right,
              zIndex: 2147483000,
            }}
            className="min-w-[160px] overflow-hidden rounded-lg border border-main bg-main py-1 shadow-lg"
          >
            {languages.map((lang) => {
              const selected = lang === value;
              return (
                <li key={lang}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      onChange(lang);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                      selected
                        ? "bg-second text-main"
                        : "text-main hover:bg-second"
                    }`}
                  >
                    <LanguageIcon language={lang} size={18} />
                    <span className="flex-1">{languageLabel(lang)}</span>
                    {selected && <CheckIcon className="h-3 w-3 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
import {
  KeyboardShortcut,
  getShortcutText,
  useIsMac,
} from "../../base/KeyboardShortcut";
import { FileTypeIcon } from "./FileTypeIcon";

// File Tree Types
export interface FileTreeItem {
  id: string;
  name: string;
  type: "file" | "folder";
  children?: FileTreeItem[];
  isOpen?: boolean;
}

// File Tab Component
interface FileTabProps {
  fileId: string;
  name: string;
  isActive: boolean;
  onClick: () => void;
  onClose: () => void;
  /** 閉じられない教材もある。押しても何も起きない × は出さない */
  closable?: boolean;
}

function FileTab({
  fileId,
  name,
  isActive,
  onClick,
  onClose,
  closable = true,
}: FileTabProps) {
  return (
    <div
      data-file-tab="true"
      data-file-id={fileId}
      data-active={String(isActive)}
      className={`relative flex items-center gap-2 px-4 py-2 cursor-pointer rounded-t-lg font-sans text-[15px] md:text-xs ${
        isActive ? "bg-main z-10" : "bg-second z-0"
      }`}
      onClick={onClick}
    >
      <FileTypeIcon name={name} />
      <span className="whitespace-nowrap text-main">{name}</span>
      {closable && (
        <button
          className="flex items-center justify-center p-0.5 bg-transparent border-none cursor-pointer text-main hover:opacity-70"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="閉じる"
        >
          <CloseIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// File Tree Item Component
interface FileTreeItemComponentProps {
  item: FileTreeItem;
  level: number;
  selectedFileId?: string;
  onFileClick: (id: string) => void;
  onToggleFolder: (id: string) => void;
}

function FileTreeItemComponent({
  item,
  level,
  selectedFileId,
  onFileClick,
  onToggleFolder,
}: FileTreeItemComponentProps) {
  const isSelected = selectedFileId === item.id;
  const isFolder = item.type === "folder";

  const handleClick = () => {
    if (isFolder) {
      onToggleFolder(item.id);
    } else {
      onFileClick(item.id);
    }
  };

  if (isFolder) {
    return (
      <div className="flex flex-col gap-2">
        <div
          className="flex items-center justify-between gap-2 px-4 py-2 cursor-pointer rounded-lg font-sans text-base bg-third border border-sub"
          onClick={handleClick}
        >
          <div className="flex items-center gap-2">
            <FolderIcon className="w-4 h-4 text-main" />
            <span className="text-main">{item.name}</span>
          </div>
          {item.isOpen ? (
            <ChevronDownIcon className="w-6 h-6 text-main" />
          ) : (
            <ChevronRightIcon className="w-6 h-6 text-main" />
          )}
        </div>
        {item.isOpen && item.children && (
          <div className="flex flex-col gap-2 pl-4">
            {item.children.map((child) => (
              <FileTreeItemComponent
                key={child.id}
                item={child}
                level={level + 1}
                selectedFileId={selectedFileId}
                onFileClick={onFileClick}
                onToggleFolder={onToggleFolder}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 px-0 py-2 cursor-pointer font-sans text-[15px] ${
        isSelected ? "text-primary" : "text-sub"
      }`}
      onClick={handleClick}
    >
      <FileTypeIcon name={item.name} className="w-4 h-4 shrink-0" />
      <span>{item.name}</span>
    </div>
  );
}

// Main CodingEditorArea Component
export interface CodingEditorAreaProps {
  /**
   * エディター下部の操作バー（保存・リセット・実行・提出など）を出すか。
   * 読み取り専用のデモや講師用表示では false にする。既定は出す。
   */
  showEditorActions?: boolean;
  /**
   * 結果欄の見出し。既定は「コードの実行結果」。
   *
   * コードを書かない教材でもこの欄は使う。セキュリティ演習は書くのが
   * コードではなく入力なので、既定のままだと「コードなんて書いていない」
   * という見出しが結果の上に出る
   */
  executionResultLabel?: string;
  /** 保存操作だけを隠す。操作バー全体は維持できる */
  showSaveAction?: boolean;
  /** 提出操作だけを隠す。操作バー全体は維持できる */
  showSubmitAction?: boolean;
  /**
   * 提出の釦の字と、待つ間の字。既定は「提出する」「採点中」。
   * 記録も採点もしない頁 (公開のコーディング面接問題) は「例で確かめる」「確かめています」に替える
   */
  submitActionLabel?: string;
  submittingLabel?: string;
  /** 実行ボタンの表示名 */
  runActionLabel?: string;

  /**
   * ファイルツリーのデータ
   */
  fileTree: FileTreeItem[];
  /**
   * 左のファイル一覧を出すか。
   * ファイルが 2 つしかない教材ではタブと同じものが二度並ぶだけなので、
   * その場合は閉じたままにする
   */
  showFileSidebar?: boolean;
  /** ファイル追加・フォルダ追加の操作を出すか */
  showFileCreateActions?: boolean;

  /**
   * 現在開いているファイルのID配列
   */
  openFiles: string[];

  /**
   * 現在アクティブなファイルのID
   */
  activeFileId?: string;

  /**
   * コードエディターの内容（ReactNodeで自由に指定可能）
   */
  editorContent?: ReactNode;

  /**
   * コード実行結果
   */
  executionResult?: ReactNode;

  /**
   * 提出の結果 (「できているか」)。提出の釦のすぐ上に置き、窓やトーストに出さない。
   * 外れの行が多いときは高さを切って中で送る
   */
  submitResult?: ReactNode;

  /**
   * スマホで頁全体の 1 本のスクロールに載せる (CodingLessonPage の縦積み)。
   * 高さを枠に合わせず中身なりにし、エディターは画面の半分を保つ。
   * 実行結果は切らずに全部出し、提出の結果は画面の 55% で切る。外れの行が多くてもエディターは縮まない
   */
  flowOnMobile?: boolean;
  /** flowOnMobile のとき、エディターの最低の高さを 50svh から 40svh に下げる。下に固定の札があり、その上に下の帯を全部見せたい頁で使う */
  compactOnMobile?: boolean;

  /**
   * エディターのフォントサイズ
   */
  fontSize?: number;

  /**
   * フォントサイズ変更時のハンドラー
   */
  onFontSizeChange?: (size: number) => void;

  /**
   * Vimモードが有効かどうか
   */
  vimMode?: boolean;

  /**
   * Vimモード変更時のハンドラー
   */
  onVimModeChange?: (enabled: boolean) => void;

  /**
   * ファイルクリック時のハンドラー
   */
  onFileClick?: (fileId: string) => void;

  /**
   * ファイルを閉じる時のハンドラー
   */
  onCloseFile?: (fileId: string) => void;

  /**
   * 新しいファイル作成時のハンドラー
   */
  onCreateFile?: () => void;

  /**
   * 新しいフォルダ作成時のハンドラー
   */
  onCreateFolder?: () => void;

  /**
   * 実行ボタンクリック時のハンドラー
   */
  onRun?: () => void;

  /**
   * 実行中かどうか
   */
  isRunning?: boolean;

  /**
   * 提出中かどうか
   */
  isSubmitting?: boolean;

  /**
   * プレビューボタンクリック時のハンドラー
   */
  onPreview?: () => void;

  /**
   * 保存ボタンクリック時のハンドラー
   */
  onSave?: () => void;

  /**
   * 提出ボタンクリック時のハンドラー
   */
  onSubmit?: () => void;

  /**
   * リセットボタンクリック時のハンドラー
   */
  onReset?: () => void;

  /**
   * 提出ボタンを無効化するか
   */
  submitDisabled?: boolean;

  /**
   * 実行・提出・リセットをまとめて押せなくする。ショートカット (⌘+Enter) も止まる。
   * 実行環境がまだ立ち上がっていないときのように、操作そのものが始められない間に使う
   */
  actionsDisabled?: boolean;

  /** 操作バーに出す一言。actionsDisabled の理由を、押せないボタンのそばで伝える */
  actionsNotice?: ReactNode;

  /** 操作バーの左の並び (言語と設定のあと) に足す釦。手書きメモの開け閉めなど、頁ごとの道具を置く */
  toolbarExtra?: ReactNode;

  /**
   * 操作バーに触れた (pointerdown か focus) ときの合図。
   * 押せない間もボタンの上の pointerdown はバーまで届く (押せないボタンは pointer-events を切ってある)
   */
  onActionsIntent?: () => void;

  /**
   * 追加のクラス名
   */
  className?: string;

  /**
   * 利用可能な言語のリスト
   */
  availableLanguages?: string[];

  /**
   * 現在選択されている言語
   */
  selectedLanguage?: string;

  /**
   * 言語変更時のハンドラー
   */
  onLanguageChange?: (language: string) => void;

  /**
   * 実行完了Snackbarを表示するかどうか
   */
  showExecutionSnackbar?: boolean;

  /**
   * 実行した結果、まだ通っていないテストがあるか。true なら緑のチェックの「実行しました」を出さない
   * (落ちても緑のチェックが出て、通ったように見えた。2026-10-28)
   */
  executionFailed?: boolean;

  /**
   * 解答があるかどうか
   */
  hasSolution?: boolean;

  /**
   * 解答を見るボタンクリック時のハンドラー
   */
  onShowSolution?: () => void;

  /**
   * 設定ボタン（フォントサイズ・テーマ・キーマップのドロップダウン）を出すか。
   * 3つのハンドラーのどれか1つでも渡っていれば出す
   */
  onSettingsClick?: () => void;
  onFontSizeClick?: () => void;
  onKeymapClick?: () => void;
}


/*
押し込みの見た目を、面の色を変えずに足す。

**裏に面を敷く手 (Button の after) は使わない。** あれは isolate で
stacking context を作るので、負の z-index を振っても親の背景より手前に出る。
実際に敷いてみたら、縁の色がボタンの面を丸ごと覆った。

代わりに下向きの影で厚みを作る。影は面の外側にしか描かれないので、
どの面の色とも干渉しない。角丸にもそのまま沿う。押している間は
影を消して本体を同じだけ下げるので、下端の位置は動かない。

深さの数値は Button の PRESS_DEPTH から取る。ここに 4 と 2 を写すと、
片方だけ直されたときに序列が崩れる。
*/
const PRESS_SHADOW = {
  /*
  **クラス名は文字列のまま持つ。** `shadow-[0_${n}px...]` のように組み立てると
  Tailwind が拾えず、影が1つも出ない。Button の PRESS_DEPTH と同じ理由
  */
  shallow: "shadow-[0_2px_0_0_var(--color-bg-third)]",
  deep: "shadow-[0_4px_0_0_var(--color-bg-primary-dark)]",
} as const;

function pressClasses(depth: keyof typeof PRESS_DEPTH) {
  return [
    PRESS_DEPTH[depth].space,
    PRESS_SHADOW[depth],
    PRESS_DEPTH[depth].press,
    "active:shadow-none",
    "motion-reduce:active:translate-y-0",
  ].join(" ");
}

export function CodingEditorArea({
  showEditorActions = true,
  executionResultLabel = "コードの実行結果",
  showSaveAction = true,
  showSubmitAction = true,
  runActionLabel = "実行する",
  submitActionLabel,
  submittingLabel = "採点中",
  fileTree,
  showFileSidebar = true,
  showFileCreateActions = true,
  openFiles,
  activeFileId,
  editorContent,
  executionResult,
  submitResult,
  flowOnMobile = false,
  compactOnMobile = false,
  fontSize: fontSizeProp,
  onFontSizeChange,
  vimMode: vimModeProp,
  onVimModeChange,
  onFileClick,
  onCloseFile,
  onCreateFile,
  onCreateFolder,
  onRun,
  isRunning = false,
  isSubmitting = false,
  onPreview,
  onSave,
  onSubmit,
  onReset,
  submitDisabled = false,
  actionsDisabled = false,
  actionsNotice,
  toolbarExtra,
  onActionsIntent,
  className = "",
  availableLanguages,
  selectedLanguage,
  onLanguageChange,
  showExecutionSnackbar = false,
  executionFailed = false,
  hasSolution = false,
  onShowSolution,
  onSettingsClick,
  onFontSizeClick,
  onKeymapClick,
}: CodingEditorAreaProps) {
  const isMac = useIsMac();
  const [localFileTree, setLocalFileTree] = useState(fileTree);
  const [selectedFileId, setSelectedFileId] = useState(activeFileId);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  /*
    メニューは画面に対して置く。エディターの枠は overflow で切っているので、
    枠の中に absolute で置くと、ボタンが枠の左に寄ったとき左半分が切れて読めない
  */
  const [settingsMenuPos, setSettingsMenuPos] = useState<{ left: number; bottom: number } | null>(null);
  const SETTINGS_MENU_WIDTH = 256;
  const toggleSettingsMenu = (button: HTMLElement) => {
    if (isSettingsMenuOpen) {
      setIsSettingsMenuOpen(false);
      return;
    }
    const rect = button.getBoundingClientRect();
    const left = Math.min(
      Math.max(8, rect.right - SETTINGS_MENU_WIDTH),
      window.innerWidth - SETTINGS_MENU_WIDTH - 8,
    );
    setSettingsMenuPos({ left, bottom: window.innerHeight - rect.top + 8 });
    setIsSettingsMenuOpen(true);
  };

  // サイドバーは常に閉じた状態から始める。以前は localStorage の値を
  // 初期状態に使っていたが、SSR (localStorage 無し・false) とクライアント
  // (localStorage 有り・true) で初回レンダリング結果が食い違い、
  // 「開いた状態が一瞬見えてから閉じる」チラつきが出ていた
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // localStorageからフォントサイズを読み込む (デフォルト: 14px)
  // propsで渡された場合はそちらを優先
  const [internalFontSize, setInternalFontSize] = useState(() => {
    try {
      const stored = localStorage.getItem("coding-editor-font-size");
      return stored !== null ? parseInt(stored, 10) : 14;
    } catch {
      return 14;
    }
  });

  // localStorageからVimモードの状態を読み込む (デフォルト: false)
  // propsで渡された場合はそちらを優先
  const [internalVimMode, setInternalVimMode] = useState(() => {
    try {
      const stored = localStorage.getItem("coding-editor-vim-mode");
      return stored !== null ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const fontSize = fontSizeProp ?? internalFontSize;
  const vimMode = vimModeProp ?? internalVimMode;

  // propsのfileTreeが変更されたらlocalFileTreeを更新
  useEffect(() => {
    setLocalFileTree(fileTree);
  }, [fileTree]);

  // 設定メニューの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(event.target as Node)
      ) {
        setIsSettingsMenuOpen(false);
      }
    };
    // 画面に対して置いているので、窓の大きさが変わったらボタンから離れる。閉じる
    const close = () => setIsSettingsMenuOpen(false);
    if (isSettingsMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", close);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", close);
    };
  }, [isSettingsMenuOpen]);

  // フォントサイズが変更されたらlocalStorageに保存
  useEffect(() => {
    if (fontSizeProp === undefined) {
      try {
        localStorage.setItem(
          "coding-editor-font-size",
          internalFontSize.toString(),
        );
      } catch (error) {
        console.error("Failed to save font size to localStorage:", error);
      }
    }
  }, [internalFontSize, fontSizeProp]);

  // Vimモードの状態が変更されたらlocalStorageに保存
  useEffect(() => {
    if (vimModeProp === undefined) {
      try {
        localStorage.setItem(
          "coding-editor-vim-mode",
          JSON.stringify(internalVimMode),
        );
      } catch (error) {
        console.error("Failed to save vim mode to localStorage:", error);
      }
    }
  }, [internalVimMode, vimModeProp]);

  // キーボードショートカット。ボタンが押せない間はキーでも押せない (ボタンと同じ条件を見る)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+Shift+Enter で提出
      if (e.metaKey && e.shiftKey && e.key === "Enter") {
        e.preventDefault();
        if (!isSubmitting && !submitDisabled && !actionsDisabled) onSubmit?.();
      }
      // Cmd+Enter で実行
      else if (e.metaKey && e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isRunning && !actionsDisabled) onRun?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSubmit, onRun, isSubmitting, submitDisabled, isRunning, actionsDisabled]);

  const handleToggleFolder = (folderId: string) => {
    const toggleFolder = (items: FileTreeItem[]): FileTreeItem[] => {
      return items.map((item) => {
        if (item.id === folderId && item.type === "folder") {
          return { ...item, isOpen: !item.isOpen };
        }
        if (item.children) {
          return { ...item, children: toggleFolder(item.children) };
        }
        return item;
      });
    };
    setLocalFileTree(toggleFolder(localFileTree));
  };

  const handleFileClick = (fileId: string) => {
    setSelectedFileId(fileId);
    onFileClick?.(fileId);
  };

  const getFileNameById = (id: string): string => {
    const findFile = (items: FileTreeItem[]): string | undefined => {
      for (const item of items) {
        if (item.id === id) return item.name;
        if (item.children) {
          const found = findFile(item.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    return findFile(localFileTree) || id;
  };

  return (
    <div
      className={`flex flex-col h-full w-full bg-main rounded-lg overflow-hidden ${flowOnMobile ? "max-md:h-auto" : ""} ${className}`}
    >
      {/* スマホでは実行結果と提出の結果の欄が伸びても、エディターを 200px より縮めない (2026-10-29、JS で書く欄が消えた) */}
      <div className={`flex flex-1 overflow-hidden gap-1 ${flowOnMobile ? (compactOnMobile ? "max-md:min-h-[40svh]" : "max-md:min-h-[50svh]") : "max-md:min-h-[200px]"}`}>
        {/* Sidebar - File Tree */}
        {showFileSidebar && isSidebarOpen && (
          <div className="w-[142px] bg-second flex flex-col pt-2 px-2 pb-2">
            {showFileCreateActions && (
              <div className="flex gap-1 mb-2 justify-end">
                <Tooltip content="新しいファイル" position="bottom-center">
                  <button
                    className="flex items-center justify-center h-8 w-8 rounded cursor-pointer text-sub hover:text-main hover:bg-third transition-colors"
                    onClick={onCreateFile}
                    aria-label="新しいファイル"
                  >
                    <FilePlusIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
                <Tooltip content="準備中" position="bottom-center">
                  <button
                    className="flex items-center justify-center h-8 w-8 rounded text-placeholder cursor-not-allowed"
                    disabled
                    aria-label="新しいフォルダ"
                  >
                    <FolderPlusIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>
            )}
            <div className="flex flex-col gap-2 flex-1 overflow-auto">
              {localFileTree.map((item) => (
                <FileTreeItemComponent
                  key={item.id}
                  item={item}
                  level={0}
                  selectedFileId={selectedFileId}
                  onFileClick={handleFileClick}
                  onToggleFolder={handleToggleFolder}
                />
              ))}
            </div>
          </div>
        )}

        {/* Editor Section */}
        <div className="flex-1 flex flex-col overflow-hidden rounded-lg">
          {/* Tab Bar
              開くファイルを持たない教材 (HTML/CSS はエディタ側にタブがある) では
              同じファイル名が二段に並んでしまうので、丸ごと出さない */}
          {openFiles.length > 0 && (
            <div className="flex items-center gap-0 p-0 bg-second overflow-x-auto rounded-t-lg">
              {showFileSidebar && (
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="flex items-center justify-center h-10 w-10 bg-second border-0 cursor-pointer text-sub hover:text-main transition-colors shrink-0"
                  aria-label={
                    isSidebarOpen ? "サイドバーを閉じる" : "サイドバーを開く"
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                  >
                    <path
                      d="M2 3h12M2 8h12M2 13h12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
              {openFiles.map((fileId) => (
                <FileTab
                  key={fileId}
                  fileId={fileId}
                  name={getFileNameById(fileId)}
                  isActive={fileId === activeFileId}
                  onClick={() => handleFileClick(fileId)}
                  onClose={() => onCloseFile?.(fileId)}
                  closable={!!onCloseFile}
                />
              ))}
            </div>
          )}

          {/* Editor Content */}
          <div
            className="flex-1 overflow-auto bg-main rounded-b-lg"
            style={{ fontSize: `${fontSize}px` }}
          >
            {isValidElement(editorContent) &&
            editorContent.type &&
            typeof editorContent.type === "function"
              ? (() => {
                  // CodeEditorコンポーネントかどうかを確認
                  const componentName = (editorContent.type as any).name;

                  // CodeEditorまたはCodeEditorPropsを持つ要素の場合、vimModeとfontSizeを追加
                  if (
                    componentName === "CodeEditor" ||
                    (editorContent.props as any)?.value !== undefined
                  ) {
                    const props = editorContent.props || {};
                    return cloneElement(editorContent as any, {
                      ...props,
                      vimMode: vimMode,
                      fontSize: fontSize,
                    });
                  }
                  return editorContent;
                })()
              : editorContent}
          </div>

          {/* Execution Result */}
          {executionResult && (
            <div className={`px-2 py-5 bg-main border-t border-main max-h-[280px] overflow-auto rounded-b-lg ${flowOnMobile ? "max-md:max-h-none" : "max-md:max-h-[22vh]"}`}>
              <div className="font-sans text-sm font-semibold text-main mb-3">
                {executionResultLabel}
              </div>
              {executionResult}
            </div>
          )}
        </div>
      </div>

      {submitResult ? (
        <div
          data-submit-result-slot=""
          className={`shrink-0 overflow-y-auto px-2 pt-2 md:max-h-[320px] ${flowOnMobile ? "max-md:max-h-[55svh]" : "max-md:max-h-[28vh]"}`}
        >
          {submitResult}
        </div>
      ) : null}

      {/* Bottom Bar */}
      {showEditorActions && (
        <div
          className="flex items-center justify-between gap-2 px-2 py-4 bg-main max-md:flex-wrap"
          onPointerDownCapture={onActionsIntent}
          onFocusCapture={onActionsIntent}
        >
          {/* 左側: リセット、保存、解答を見る */}
          <div className="flex items-center gap-2">
            {/* リセットボタン - アイコンのみ */}
            {onReset && (
              <Tooltip content="初期コードに戻す" position="top-center">
                <button
                  className={`h-10 w-10 p-2.5 rounded-lg border border-main inline-flex justify-center items-center text-primary hover:bg-second active:bg-third transition-colors cursor-pointer bg-main ${actionsDisabled ? "pointer-events-none opacity-40" : ""}`}
                  onClick={() => setShowResetModal(true)}
                  disabled={actionsDisabled}
                  aria-label="初期コードに戻す"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.35645 9.43085C2.35822 9.4309 2.35874 9.43081 2.35645 9.43085V9.43085ZM18.438 15.5417C18.438 15.965 18.1154 16.3134 17.7031 16.3555L17.6193 16.3604C17.1676 16.3603 16.8014 15.9931 16.8014 15.5417V13.2753L14.9647 15.0013C14.1258 15.8346 13.0914 16.4451 11.9552 16.7738C10.8109 17.1047 9.60059 17.1399 8.4388 16.8771C7.27712 16.6142 6.20083 16.061 5.31055 15.2699C4.42012 14.4785 3.74348 13.4739 3.34603 12.3508C3.19558 11.9251 3.4193 11.4573 3.84489 11.3067C4.27052 11.1564 4.73839 11.3799 4.889 11.8055C5.19424 12.6679 5.71249 13.439 6.39616 14.0467C7.07999 14.6545 7.90699 15.0793 8.79932 15.2813C9.69179 15.4832 10.6215 15.4565 11.5003 15.2023C12.3792 14.9481 13.1793 14.4742 13.8262 13.827C13.833 13.8202 13.8421 13.8126 13.8449 13.8099L15.5547 12.2035H13.4632C13.0118 12.2035 12.6447 11.8373 12.6445 11.3856C12.6445 10.934 13.0116 10.5669 13.4632 10.5669H17.6193C17.627 10.5669 17.634 10.5676 17.6364 10.5677C17.6501 10.568 17.6647 10.5693 17.6771 10.5702C17.6797 10.5703 17.682 10.5699 17.6877 10.5702C17.6924 10.5704 17.6995 10.571 17.7072 10.5718C17.7776 10.5794 17.8468 10.5972 17.9123 10.6223L18.0132 10.6678L18.1247 10.7435C18.1247 10.7435 18.1278 10.7458 18.1312 10.7484C18.1339 10.7504 18.1381 10.7536 18.1426 10.7574C18.1563 10.7688 18.1707 10.7827 18.1776 10.7891L18.2093 10.82L18.215 10.8257L18.2231 10.8347L18.224 10.8363C18.235 10.8484 18.2471 10.862 18.259 10.877C18.2677 10.888 18.2768 10.9002 18.2858 10.9128C18.2923 10.9219 18.2988 10.9309 18.3045 10.9396C18.3124 10.9518 18.3195 10.964 18.3257 10.9746C18.3315 10.9846 18.34 10.9987 18.3477 11.0137C18.3507 11.0196 18.3563 11.0318 18.3615 11.043L18.3794 11.0845C18.3844 11.0973 18.3896 11.1111 18.394 11.1244L18.4054 11.1626C18.4068 11.1675 18.4101 11.1796 18.4128 11.1903C18.4174 11.2093 18.4207 11.2282 18.4233 11.2432C18.4251 11.2532 18.4268 11.2638 18.4282 11.2741C18.4295 11.2838 18.4318 11.299 18.4331 11.314C18.4342 11.3267 18.4358 11.3416 18.4364 11.3571C18.4364 11.3571 18.4361 11.3592 18.4364 11.362C18.4367 11.3661 18.438 11.3753 18.438 11.3856V15.5417ZM1.79899 9.18915L1.79655 9.18753C1.79339 9.18496 1.78806 9.18019 1.78271 9.17451C1.7782 9.1697 1.77458 9.16509 1.77295 9.16311L1.77132 9.16148L1.68994 9.05406C1.68342 9.04385 1.67599 9.03169 1.66878 9.01907C1.66224 9.00763 1.65641 8.99571 1.65251 8.98814C1.6462 8.97585 1.63917 8.96197 1.63623 8.95559L1.63704 8.95478C1.63179 8.9434 1.62292 8.92586 1.61589 8.90758C1.61292 8.89985 1.60986 8.89241 1.60775 8.88642C1.60347 8.87432 1.59625 8.85437 1.59066 8.83434L1.58415 8.80667C1.57984 8.78881 1.57645 8.7712 1.57357 8.75458C1.57357 8.75458 1.57188 8.74946 1.57113 8.74482V8.74319L1.56543 8.70331L1.56462 8.69843C1.56375 8.68995 1.56346 8.68119 1.56299 8.67483C1.56241 8.66688 1.56226 8.65946 1.56217 8.65449C1.56207 8.64858 1.56147 8.6461 1.56136 8.64309C1.56122 8.63944 1.56055 8.62515 1.56055 8.61461V4.45771C1.56055 4.00608 1.92684 3.6399 2.37842 3.63984L2.46224 3.64472C2.87446 3.68669 3.19629 4.03438 3.19629 4.45771V6.72252L5.03304 4.99807C5.8719 4.16485 6.90632 3.55433 8.04248 3.22561C9.18679 2.89462 10.3971 2.85948 11.5589 3.12226C12.7207 3.38514 13.7976 3.93824 14.688 4.72952C15.5784 5.52091 16.2542 6.52562 16.6517 7.64863C16.8022 8.07439 16.5785 8.54211 16.1528 8.69273C15.7272 8.84295 15.2601 8.61936 15.1095 8.19387C14.8043 7.33148 14.2853 6.56046 13.6016 5.95266C12.9177 5.34487 12.0908 4.92008 11.1984 4.71813C10.306 4.51624 9.37631 4.54368 8.4974 4.79788C7.61858 5.05216 6.81835 5.52529 6.17155 6.17239C6.16482 6.17911 6.15567 6.18681 6.15283 6.18948L4.44303 7.79592H6.53532C6.98702 7.79603 7.35319 8.16318 7.35319 8.61461C7.35299 9.06599 6.98678 9.43237 6.53532 9.43248H2.37842C2.37228 9.43248 2.36694 9.43202 2.36296 9.43167C2.35935 9.43134 2.35573 9.43107 2.354 9.43085L2.35238 9.43004C2.35049 9.43003 2.34825 9.43013 2.34587 9.43004C2.32608 9.42926 2.30211 9.42819 2.27751 9.42516L2.26042 9.42272L2.25879 9.4219C2.24116 9.41934 2.22178 9.41654 2.20182 9.41214C2.197 9.41107 2.19265 9.40966 2.18962 9.40888C2.18593 9.40793 2.18411 9.4077 2.18229 9.40725C2.16457 9.40288 2.14435 9.39775 2.1237 9.39098C2.11337 9.38759 2.10267 9.38335 2.09684 9.38121C2.05802 9.36695 2.01925 9.34974 1.9821 9.32913C1.97081 9.32284 1.95987 9.31606 1.95199 9.31122C1.94048 9.30416 1.92575 9.29526 1.9113 9.28518L1.88444 9.26484L1.79899 9.18915Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </Tooltip>
            )}

            {/* 保存ボタン - アイコンのみ */}
            {showSaveAction && (
              <Tooltip content="コードを保存する" position="top-center">
                <button
                  className="h-10 w-10 p-2.5 rounded-lg border border-main inline-flex justify-center items-center text-primary hover:bg-second active:bg-third transition-colors cursor-pointer bg-main"
                  onClick={onSave}
                  aria-label="保存"
                >
                  <SaveIcon className="w-5 h-5 text-primary-400" />
                </button>
              </Tooltip>
            )}

            {/* 解答を見るボタン */}
            {hasSolution && onShowSolution && (
              <Tooltip content="解答を見る" position="top-center">
                <button
                  className="h-10 px-2.5 gap-2 rounded-lg border border-main inline-flex justify-center items-center text-primary hover:bg-second active:bg-third transition-colors cursor-pointer bg-main"
                  onClick={onShowSolution}
                  aria-label="解答を見る"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.99902 18.9993C9.99902 20.1039 10.8944 20.9993 11.999 20.9993C13.1036 20.9993 13.999 20.1039 13.999 18.9993H9.99902ZM17.999 9.99933C17.999 6.68562 15.3127 3.99933 11.999 3.99933C8.68532 3.99933 5.99902 6.68562 5.99902 9.99933C5.99902 12.1806 7.1623 14.0914 8.90723 15.1429C9.51626 15.5099 9.99179 16.1819 9.99805 16.9993H14C14.0063 16.1819 14.4817 15.5099 15.0908 15.1429C16.8357 14.0914 17.999 12.1806 17.999 9.99933ZM19.999 9.99933C19.999 12.9105 18.4435 15.4574 16.123 16.8558C16.0737 16.8855 16.0398 16.9221 16.0205 16.9534C16.0024 16.9829 15.999 17.0038 15.999 17.0159V18.9993C15.999 21.2085 14.2082 22.9993 11.999 22.9993C9.78984 22.9993 7.99902 21.2085 7.99902 18.9993V17.0159L7.99512 16.9915C7.99213 16.9811 7.98667 16.9683 7.97754 16.9534C7.95826 16.9221 7.9243 16.8855 7.875 16.8558C5.55453 15.4574 3.99902 12.9105 3.99902 9.99933C3.99902 5.58105 7.58075 1.99933 11.999 1.99933C16.4173 1.99933 19.999 5.58105 19.999 9.99933Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </Tooltip>
            )}

            {/* 言語セレクター */}
            {availableLanguages && availableLanguages.length > 1 && (
              <div className="flex items-center gap-1 ml-2 pl-2 border-l border-main">
                <LanguagePicker
                  languages={availableLanguages}
                  value={selectedLanguage || availableLanguages[0]}
                  onChange={(lang) => onLanguageChange?.(lang)}
                />
              </div>
            )}

            {/* 設定ボタン & ドロップダウンメニュー（フォントサイズ・テーマ・キーマップ）。
              以前はヘッダー側にあったが、エディター本体の設定なのでツールバーに移した */}
            {(onSettingsClick || onFontSizeClick || onKeymapClick) && (
              <div className="relative" ref={settingsMenuRef}>
                <Tooltip content="エディターの設定" position="top-center">
                  <button
                    onClick={(e) => toggleSettingsMenu(e.currentTarget)}
                    className="h-10 w-10 p-2.5 rounded-lg border border-main inline-flex justify-center items-center text-primary hover:bg-second active:bg-third transition-colors cursor-pointer bg-main"
                    aria-label="エディターの設定"
                  >
                    <SettingsIcon className="w-4 h-4 text-primary" />
                  </button>
                </Tooltip>

                {isSettingsMenuOpen && settingsMenuPos && (
                  <div
                    className="fixed w-64 bg-surface rounded-xl shadow-lg border border-main overflow-hidden z-50"
                    style={settingsMenuPos}
                  >
                    <div className="px-4 py-3 border-b border-main">
                      <span className="font-bold text-sm text-main">
                        エディターの設定
                      </span>
                    </div>
                    <div className="py-1">
                      {/* フォントサイズ */}
                      <button
                        onClick={() => {
                          setIsSettingsMenuOpen(false);
                          onFontSizeClick?.();
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-second transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className="w-5 h-5 text-sub"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M4 6h16M4 12h16m-7 6h7"
                            />
                          </svg>
                          <span className="text-sm text-main">
                            フォントサイズ
                          </span>
                        </div>
                      </button>
                      {/* エディターのテーマ */}
                      <button
                        onClick={() => {
                          setIsSettingsMenuOpen(false);
                          onSettingsClick?.();
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-second transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className="w-5 h-5 text-sub"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="text-sm text-main">
                            エディターのテーマ
                          </span>
                        </div>
                      </button>
                      {/* キーマップ変更 */}
                      <button
                        onClick={() => {
                          setIsSettingsMenuOpen(false);
                          onKeymapClick?.();
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-second transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className="w-5 h-5 text-sub"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                            />
                          </svg>
                          <span className="text-sm text-main">
                            キーマップ変更
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {toolbarExtra}
          </div>

          {actionsNotice && (
            <p
              role="status"
              className="min-w-0 flex-1 text-[15px] md:text-xs leading-tight text-sub"
            >
              {actionsNotice}
            </p>
          )}

          {/* 右側: 実行、提出 */}
          <div className="flex items-center gap-2 relative max-md:ml-auto">
            {/* 実行完了Snackbar */}
            <div className="absolute bottom-full right-0 mb-2">
              <Snackbar
                message={executionFailed ? "まだ通っていないテストがあります" : "実行しました"}
                icon={executionFailed ? "none" : "check"}
                isVisible={showExecutionSnackbar}
              />
            </div>
            {/* 実行するボタン - テキスト付き
              書いた先から描き直す教材 (HTML/CSS) は onRun を渡さない。
              押しても何も起きないボタンを置くと、押さないと動かないと思われる */}
            {onRun && (
              <Tooltip
                content={runActionLabel}
                position="top-center"
                disabled={isRunning || showExecutionSnackbar}
              >
                {/* 待機している操作なので沈みは 2px。いちばん押してほしい提出だけ 4px */}
<button
                  /* 面の色は変えない。厚みだけ足す。待機している操作なので 2px */
                  className={`h-10 px-4 gap-2 rounded-lg border border-main inline-flex justify-center items-center text-primary hover:bg-second active:bg-third transition-colors disabled:opacity-40 cursor-pointer bg-main ${isRunning ? "" : pressClasses("shallow")} ${actionsDisabled ? "pointer-events-none" : ""}`}
                  onClick={onRun}
                  disabled={isRunning || actionsDisabled}
                  aria-label={runActionLabel}
                >
                  {isRunning ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <RightRunIcon className="w-5 h-5 text-primary-400" />
                  )}
                  <span className="font-sans text-[15px] md:text-sm font-bold leading-none text-primary">
                    {runActionLabel}
                  </span>
                  <KeyboardShortcut shortcut="run" />
                </button>
              </Tooltip>
            )}

            {/* 提出するボタン - テキスト付き、緑背景 */}
            {showSubmitAction && (
              <Tooltip
                content={`${submitActionLabel ?? "プログラムを提出する"} (${getShortcutText("submit", isMac)})`}
                position="top-center"
              >
                {/* この画面でいちばん押してほしい操作なので、厚みは深い側の 4px */}
                <button
                  className={`h-10 px-4 gap-2 rounded-lg inline-flex justify-center items-center text-white transition-all ${
                    isSubmitting || submitDisabled || actionsDisabled
                      ? "opacity-40 cursor-not-allowed bg-primary pointer-events-none"
                      : `cursor-pointer bg-primary hover:brightness-110 active:brightness-95 ${pressClasses("deep")}`
                  }`}
                  onClick={onSubmit}
                  disabled={isSubmitting || submitDisabled || actionsDisabled}
                  aria-label={submitActionLabel ?? "提出"}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span className="font-sans text-[15px] md:text-sm font-bold leading-none">
                        {submittingLabel}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      <span className="font-sans text-[15px] md:text-sm font-bold leading-none">
                        {submitActionLabel ?? "提出する"}
                      </span>
                    </>
                  )}
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      )}

      {/* 初期コードに戻す確認モーダル */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="初期コードに戻す"
        size="sm"
      >
        <div className="pb-2">
          <p className="mb-6 text-[17px] leading-relaxed text-main md:text-base">
            現在のコードを破棄して、初期コードに戻します。この操作は元に戻せません。
          </p>
          {/* スマホでは 2 つが横に入らないので、全幅で縦に積む (押す側を上) */}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end [&>button]:whitespace-nowrap [&>button]:max-sm:w-full [&>button]:sm:!px-5">
            <Button
              variant="ghost"
              size="lg"
              /* 隣の危険の釦と高さをそろえる (縁 2px のぶん) */
              className="border-2 border-transparent"
              onClick={() => setShowResetModal(false)}
            >
              キャンセル
            </Button>
            {/* 戻せない操作なので緑にしない。縁と字だけを危険の色にする (LgConfirm の danger と同じ形) */}
            <Button
              variant="destructive"
              size="lg"
              className="border-2 border-red-700 !bg-surface !text-red-700 hover:!bg-red-50 focus-visible:!outline-solid focus-visible:!outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 [[data-theme=dark]_&]:!border-[#f47365] [[data-theme=dark]_&]:!text-[#f47365] [[data-theme=dark]_&]:hover:!bg-second"
              /* 確認を開いたまま実行や提出を始めた場合に、リセットを重ねない */
              disabled={actionsDisabled}
              onClick={() => {
                onReset?.();
                setShowResetModal(false);
              }}
            >
              初期コードに戻す
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
